import React from "react"

import { Button } from "@coval/ui"
import { CalendarSearch } from "lucide-react"

import {
  calculatePresetDateRange,
  getPresetLabel,
} from "@/utils/date/dateRange"
import { formatDateRangeLabel } from "@/utils/date/dateRangeLabel"
import { DATE_RANGE_PRESET } from "@/utils/date/dateRangeTypes"

import { useGlobalDateRangeOverride } from "../hooks/useGlobalDateRangeOverride"
import type { GlobalDateRange } from "../types/core"

/** The widest Quick Select preset, offered as the one-click way to look further back. */
const WIDER_RANGE_PRESET = DATE_RANGE_PRESET.LAST_30_DAYS

/**
 * How far before now a range may end and still count as running to now. A
 * rolling preset's end is fixed when it is read, so it trails the clock while
 * the dashboard stays open; a range ending earlier is a past window the wider
 * preset would replace rather than extend.
 */
const ENDS_NOW_TOLERANCE_MS = 60 * 60 * 1000

interface WidgetNoDataStateProps {
  /** The range the widget queried, named in the message so the empty result reads as scoped to it. */
  dateRange: GlobalDateRange
}

/**
 * A configured widget whose query returned nothing. Names the range it looked
 * at and points to where more data may be: a wider range when one is a click
 * away (the range runs to now and starts inside the wider window), otherwise
 * the date picker and the widget's own filters.
 */
export const WidgetNoDataState: React.FC<WidgetNoDataStateProps> = ({
  dateRange,
}) => {
  const { setGlobalOverride } = useGlobalDateRangeOverride()
  const rangeLabel = formatDateRangeLabel(dateRange)
  const widerRangeLabel = getPresetLabel(WIDER_RANGE_PRESET)
  const widerRange = calculatePresetDateRange(WIDER_RANGE_PRESET)
  const canWidenRange =
    dateRange.start.getTime() > widerRange.start.getTime() &&
    dateRange.end.getTime() >= widerRange.end.getTime() - ENDS_NOW_TOLERANCE_MS

  return (
    <div
      className="flex h-full flex-col items-center justify-center gap-1 px-4 text-center"
      data-testid="widget-no-data-state"
    >
      <CalendarSearch
        className="mb-1 size-6 text-coval-text-muted/60"
        strokeWidth={1.5}
        aria-hidden="true"
      />
      <p className="text-sm font-medium text-coval-text">
        {rangeLabel ? `No data for ${rangeLabel}` : "No data in this range"}
      </p>
      <p className="max-w-xs text-xs text-coval-text-muted">
        {canWidenRange
          ? "Older data may fall outside this range."
          : "Pick a different date range, or loosen this widget's filters."}
      </p>
      {canWidenRange && (
        <Button
          variant="secondary"
          className="mt-2"
          onClick={() => setGlobalOverride(WIDER_RANGE_PRESET)}
        >
          Show {widerRangeLabel.toLowerCase()}
        </Button>
      )}
    </div>
  )
}
